/**
 * Order the approver's detail drawer steps through with the arrow keys (or J/K).
 *
 * This deliberately mirrors *rendered* order rather than any single sorted array:
 * the inbox draws one table per team group, so a globally-sorted flat list would
 * step in an order nobody sees on screen. Collapsed groups drop out — moving the
 * drawer onto a row that isn't visible reads as a bug.
 */

interface StepGroup {
  teamId: string;
  isUnassigned: boolean;
}

interface StepEntry {
  userId: string;
  teamId: string | null;
}

export function buildDrawerStepOrder(
  groups: readonly StepGroup[],
  filteredEntries: readonly StepEntry[],
  collapsedTeamIds: ReadonlySet<string>,
): string[] {
  return groups
    .filter((group) => !collapsedTeamIds.has(group.teamId))
    .flatMap((group) =>
      filteredEntries
        .filter((e) => (group.isUnassigned ? e.teamId === null : e.teamId === group.teamId))
        .map((e) => e.userId),
    );
}

/**
 * The neighbouring user id in step order, or null at either end.
 *
 * Clamps rather than wraps: jumping from the last sheet back to the first reads
 * as a bug when the list is long.
 */
export function stepToUserId(
  order: readonly string[],
  currentUserId: string | null,
  delta: -1 | 1,
): string | null {
  if (currentUserId === null) return null;
  const i = order.indexOf(currentUserId);
  if (i === -1) return null;
  return order[i + delta] ?? null;
}
